import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createChannel, type OrgMember } from '../../services/chatChannelService';
import { useAuthStore } from '../../store/authStore';
import MemberSearchInput from './MemberSearchInput';
import Spinner from '../ui/Spinner';

interface Props {
  onClose: () => void;
}

export default function CreateChannelModal({ onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Close on ESC
  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const handleAdd = (member: OrgMember) => {
    setMemberIds((prev) => prev.includes(member.userId) ? prev : [...prev, member.userId]);
  };

  const handleRemove = (userId: string) => {
    setMemberIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('채널 이름을 입력하세요.'); return; }
    setLoading(true);
    try {
      const channel = await createChannel({
        type: 'USER',
        name: name.trim(),
        description: description.trim() || undefined,
        memberIds,
      });
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      toast.success('채널이 생성되었습니다.');
      onClose();
      navigate(`/chat/${channel.id}`);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '채널 생성에 실패했습니다.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">새 채널 만들기</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="size-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">채널 이름 *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={50}
              placeholder="예: 디자인 팀 회의"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">설명 (선택)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} rows={2}
              placeholder="채널 설명을 입력하세요"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">멤버 초대</label>
            <MemberSearchInput
              selectedIds={memberIds}
              onAdd={handleAdd}
              onRemove={handleRemove}
              excludeIds={currentUser ? [currentUser.id] : []}
            />
            {currentUser && (
              <p className="text-sm text-gray-400 mt-1">본인({currentUser.name})은 자동 포함됩니다.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            취소
          </button>
          <button onClick={handleSubmit} disabled={!name.trim() || loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
            {loading && <Spinner className="size-4 text-white" />}
            채널 만들기
          </button>
        </div>
      </div>
    </div>
  );
}
